import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getGlobalDealerName } from './globalState';
import { savePhoto } from './photoDB';
import { getBrand } from './brands';
import { MAX_PHOTOS } from './limits';
import './App.css';

function Photography() {
  /*
  Photography component allows users to take photos of vehicles with overlays and level indicators.
  It handles camera access, photo capture, and displays overlays based on the selected dealership brand.
  It also includes device orientation detection to ensure the camera is used in landscape mode.
  */
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const initialPhotoKeys = location.state?.photoKeys || [];
  const [photoKeys, setPhotoKeys] = useState(initialPhotoKeys);
  const [flash, setFlash] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true);
  const [overlayOpacity, setOverlayOpacity] = useState(1);
  const [groundOverlayOpacity, setGroundOverlayOpacity] = useState(1);
  const [showGroundOverlay, setShowGroundOverlay] = useState(true);
  const [levelStatus, setLevelStatus] = useState('level');
  const [cameraStarted, setCameraStarted] = useState(false);
  const [saveError, setSaveError] = useState('');

  const dealer = getGlobalDealerName();
  // Brands without these assets simply don't get the feature - showing
  // another brand's vehicle would be worse than showing none.
  const overlaySrc = getBrand(dealer)?.overlay;
  const groundOverlaySrc = getBrand(dealer)?.groundOverlay;

  // Camera start/stop logic
  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          width: { ideal: 3840 },
          height: { ideal: 2160 },
          facingMode: 'environment',
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.warn('Back camera not available, falling back to front camera:', error);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (fallbackError) {
        console.error('Error accessing the camera:', fallbackError);
      }
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
    }
  };

  // Orientation detection
  useEffect(() => {
    const checkOrientation = () => {
      let isLandscapeMode = false;
      if (window.screen.orientation && window.screen.orientation.type) {
        isLandscapeMode = window.screen.orientation.type.startsWith('landscape');
      } else if (typeof window.orientation !== 'undefined') {
        isLandscapeMode = Math.abs(window.orientation) === 90;
      } else {
        isLandscapeMode = window.innerWidth > window.innerHeight;
      }
      setIsLandscape(isLandscapeMode);
    };

    checkOrientation();

    window.addEventListener('orientationchange', checkOrientation);
    window.addEventListener('resize', checkOrientation);

    return () => {
      window.removeEventListener('orientationchange', checkOrientation);
      window.removeEventListener('resize', checkOrientation);
    };
  }, []);

  // Camera start/stop only after user gesture
  useEffect(() => {
    if (isLandscape && cameraStarted) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isLandscape, cameraStarted]);

  // Device orientation/level logic with permission handling
  useEffect(() => {
    let permissionGranted = false;

    const handleOrientation = (event) => {
      if (!permissionGranted && typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        return;
      }
      const { beta, gamma } = event;
      const betaTarget = 0;
      const gammaTarget = -90;
      const betaThreshold = 5;
      const gammaThreshold = 5;
      const isBetaLevel = Math.abs(beta - betaTarget) < betaThreshold;
      const isGammaLevel = Math.abs(gamma - gammaTarget) < gammaThreshold;
      if (isBetaLevel && isGammaLevel) {
        setLevelStatus('level');
      } else {
        setLevelStatus('not-level');
      }
    };

    async function requestOrientationPermission() {
      if (
        typeof DeviceOrientationEvent !== "undefined" &&
        typeof DeviceOrientationEvent.requestPermission === "function"
      ) {
        try {
          const response = await DeviceOrientationEvent.requestPermission();
          if (response === "granted") {
            permissionGranted = true;
            window.addEventListener("deviceorientation", handleOrientation, true);
          }
        } catch (e) {
          
        }
      } else {
        window.addEventListener("deviceorientation", handleOrientation, true);
      }
    }

    // Declared at effect scope so the cleanup below removes the SAME
    // function reference that was added - removeEventListener is a no-op
    // if the references differ.
    const requestOnUserGesture = () => {
      requestOrientationPermission();
      window.removeEventListener("touchend", requestOnUserGesture);
      window.removeEventListener("click", requestOnUserGesture);
    };

    if (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function"
    ) {
      window.addEventListener("touchend", requestOnUserGesture);
      window.addEventListener("click", requestOnUserGesture);
    } else {
      window.addEventListener("deviceorientation", handleOrientation, true);
    }

    return () => {
      window.removeEventListener("deviceorientation", handleOrientation, true);
      window.removeEventListener("touchend", requestOnUserGesture);
      window.removeEventListener("click", requestOnUserGesture);
    };
  }, []);

  // User gesture to start camera
  const handleStartCamera = async () => {
    setCameraStarted(true);
  };

  const takePhoto = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (canvas && video) {
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      // High-quality JPEG: visually transparent for retouching, but a
      // fraction of the size of a full-resolution PNG.
      const photoData = canvas.toDataURL('image/jpeg', 0.92);
      try {
        const key = await savePhoto(photoData);
        setPhotoKeys(prev => [...prev, key]);
        setSaveError('');
        setFlash(true);
        setTimeout(() => setFlash(false), 200);
      } catch (err) {
        console.error('Failed to save photo:', err);
        setSaveError('This photo could not be saved - your device storage may be full. Free up space and try again.');
      }
    }
  };

  const goToGallery = () => {
    navigate('/photo-gallery', { state: { photoKeys } });
  };

  const handleToggleOverlay = () => {
    setOverlayOpacity((prev) => {
      if (prev === 1) return 0.5;
      if (prev === 0.5) return 0;
      return 1;
    });
  };

  const handleToggleGroundOverlay = () => {
    setShowGroundOverlay(prev => !prev);
    setGroundOverlayOpacity(prev => (prev === 1 ? 0 : 1));
  };

  if (!isLandscape) {
    return (
      <div className="rotate-device">
        <div>
          <img
            src="/components/icons/Rotate_Device_icon.png"
            alt="Rotate Device Illustration"
            className="rotate-device-image"
          />
          <p>Please rotate your device to left landscape mode to use the camera.</p>
        </div>
      </div>
    );
  }

  if (!cameraStarted) {
    return (
      <div className="start-camera-overlay">
        <button className="App-button" onClick={handleStartCamera}>
          Start Camera
        </button>
        <p>Tap to enable camera and level features.</p>
      </div>
    );
  }

  return (
    <div className="Photography">
      <div className="App-header">
        {/* header content */}
      </div>
      <div className="Photography-layout">
        <div className="Photography-box-area">
          <div className="Photography-box">
            {/* Level indicator */}
            <div className={`level-indicator ${levelStatus}`}>
              {levelStatus === 'level' ? '🟢 Level' : '🔴 Not Level'}
            </div>
            {/* Photo overlay image */}
            {overlaySrc && (
              <img
                src={overlaySrc}
                alt="Photo Overlay"
                className="photo-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 2,
                  opacity: overlayOpacity,
                  transition: 'opacity 0.2s',
                }}
              />
            )}
            {/* Ground overlay image */}
            {groundOverlaySrc && showGroundOverlay && (
              <img
                src={groundOverlaySrc}
                alt="Ground Overlay"
                className="photo-ground-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 3,
                  opacity: groundOverlayOpacity,
                  transition: 'opacity 0.2s',
                }}
              />
            )}
            <video ref={videoRef} autoPlay playsInline className="Photography-video"></video>
            <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
            {flash && <div className="flash-overlay"></div>}
            {saveError && (
              <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, zIndex: 4, background: 'rgba(180,0,0,0.85)', color: 'white', padding: '8px 12px', borderRadius: '8px' }}>
                {saveError}
              </div>
            )}
          </div>
        </div>
        <div className="Photography-controls-area">
          {/* Live shot counter — updates on every capture */}
          <div className="photo-counter">
            📷 {photoKeys.length} / ∞
            {photoKeys.length > 0 && (
              <span className="photo-counter-sub">select up to {MAX_PHOTOS} to submit</span>
            )}
          </div>
          <div className="Photography-controls">
            <button className="Photography-button take-photo" onClick={takePhoto}></button>
            {overlaySrc && (
              <button
                className="Photography-button toggle-overlay"
                onClick={handleToggleOverlay}
              >
                Vehicle Overlay
              </button>
            )}
            {groundOverlaySrc && (
              <button
                className="Photography-button toggle-ground-overlay"
                onClick={handleToggleGroundOverlay}
              >
                Ground Overlay
              </button>
            )}
            <button
              className="Photography-button view-gallery"
              onClick={goToGallery}
            >
              View Gallery
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Photography;

